// @flow
import React, { useState, type Node } from 'react';
import { useTranslation } from 'react-i18next';
import {
    Tabs,
    Tab,
    TabText,
    Box,
    Flex,
    Text,
    Button,
    Position
} from '@locus-taxy/neo-ui';
import Conversations from 'common/modules/chat/components/Conversations';
import Contacts from 'common/modules/chat/components/Contacts';
import ChatWindow from 'common/modules/chat/components/ChatWindow';
import useChatGlobalState from 'common/modules/chat/hooks/useChatGlobalState';
import NumberUtils from 'common/utils/NumberUtils';
import { appBarHeaderZIndex } from 'common/components/core/app_bar/HeaderWithLogout';
import testIds from 'common/modules/chat/tests/chat.json';

type ChatDrawerProps = {
    selectedChannel: ?string,
    selectedTabIndex: number,
    isMessageWindowOpen: boolean,
    onTabSelect: (tabIndex: number) => void,
    onChannelSelect: (channelUrl: ?string) => void,
    handleMessageWindowOpen: () => void,
    handleMessageWindowClose: () => void,
    onCloseChatWindow: () => void
};

const ChatDrawer = ({
    selectedChannel,
    selectedTabIndex,
    isMessageWindowOpen,
    onTabSelect,
    onChannelSelect,
    handleMessageWindowOpen,
    handleMessageWindowClose,
    onCloseChatWindow
}: ChatDrawerProps): Node => {
    const { isChatWindowOpen, unreadChannelCount } = useChatGlobalState();
    const [lastUnsentMessage, setLastUnsentMessage] = useState(null);
    const { t } = useTranslation('liveView');

    const openContactTab = () => onTabSelect(0);

    const onTabChange = (_, val: number) => {
        onTabSelect(val);
    };

    const handleMessageWindowBackAction = () => {
        handleMessageWindowClose();
        onChannelSelect(null);
    };

    const isContactTabSelected = selectedTabIndex === 0;
    const isConversationTabSelected = selectedTabIndex === 1;

    return isChatWindowOpen ? (
        <Position
            position="fixed"
            right="12px"
            bottom="64px"
            height="524px"
            width="322px"
            zIndex={appBarHeaderZIndex - 1}
            border="1px solid"
            borderColor="grey.200"
            borderRadius="8px"
            boxShadow="hovered"
            overflow="hidden"
        >
            <Flex
                backgroundColor="grey.100"
                zIndex={appBarHeaderZIndex - 1}
                width="100%"
                height="100%"
                flexDirection="column"
            >
                {!isMessageWindowOpen && (
                    <Box
                        padding={3}
                        borderBottomColor="grey.200"
                        borderBottomWidth="thin"
                        borderBottomStyle="solid"
                        backgroundColor="white"
                    >
                        <Flex
                            justifyContent="space-between"
                            alignItems="center"
                        >
                            <Text
                                fontSize={2}
                                fontWeight={600}
                                color="grey.900"
                                data-test-id={testIds.chatLabel}
                            >
                                {t('riderManagerChat.Chat')}
                            </Text>
                            <Button
                                appearance="icon"
                                icon="cross"
                                onClick={onCloseChatWindow}
                                data-test-id={testIds.chatLabelCrossBtn}
                            />
                        </Flex>
                    </Box>
                )}
                <Flex flex="1" minHeight="0" flexDirection="column">
                    {!isMessageWindowOpen && (
                        <Flex width="100%" height="100%" flexDirection="column">
                            <Tabs
                                value={selectedTabIndex}
                                onChange={onTabChange}
                                centered
                                maximized
                            >
                                <Tab value={0}>
                                    <TabText
                                        fontSize={2}
                                        data-test-id={testIds.chatContactTab}
                                    >
                                        {t('riderManagerChat.Contacts')}
                                    </TabText>
                                </Tab>
                                <Tab value={1}>
                                    <Flex alignItems="center">
                                        <TabText
                                            fontSize={2}
                                            data-test-id={
                                                testIds.chatConversationTab
                                            }
                                        >
                                            {t(
                                                'riderManagerChat.Conversations'
                                            )}
                                        </TabText>
                                        {!!unreadChannelCount && (
                                            <Text
                                                backgroundColor="blue.100"
                                                color="grey.900"
                                                fontSize="10px"
                                                lineHeight="12px"
                                                px={1}
                                                ml={3}
                                                fontWeight="600"
                                                borderRadius="8px"
                                            >
                                                {NumberUtils.padLeft(
                                                    unreadChannelCount
                                                )}
                                            </Text>
                                        )}
                                    </Flex>
                                </Tab>
                            </Tabs>
                            {isContactTabSelected && (
                                <Contacts
                                    setSelectedProfile={onChannelSelect}
                                    openMessageWindow={handleMessageWindowOpen}
                                />
                            )}
                            {isConversationTabSelected && (
                                <Flex
                                    mt={2}
                                    flexDirection="column"
                                    flex="1"
                                    minHeight="0"
                                >
                                    <Conversations
                                        setSelectedProfile={onChannelSelect}
                                        openMessageWindow={
                                            handleMessageWindowOpen
                                        }
                                        openContactTab={openContactTab}
                                    />
                                </Flex>
                            )}
                        </Flex>
                    )}
                    {isMessageWindowOpen && selectedChannel && (
                        <ChatWindow
                            closeMessageWindow={handleMessageWindowBackAction}
                            selectedProfile={selectedChannel}
                            lastUnsentMessage={lastUnsentMessage}
                            setLastUnsentMessage={setLastUnsentMessage}
                        />
                    )}
                </Flex>
            </Flex>
        </Position>
    ) : null;
};

export default ChatDrawer;
